import { Children, isValidElement, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './OverviewTable.css';

export default function OverviewTable({ children }) {
  const container = useRef(null);
  const [pinned, setPinned] = useState(null);
  const header = Children.toArray(children.props.children).find(child => isValidElement(child) && child.type === 'thead');

  useLayoutEffect(() => {
    const scroller = container.current;
    const table = scroller?.querySelector('table');
    const head = table?.tHead;
    if (!head) return undefined;
    let frame = null;
    const measure = () => {
      frame = null;
      const bounds = scroller.getBoundingClientRect();
      const tableBounds = table.getBoundingClientRect();
      const headBounds = head.getBoundingClientRect();
      const main = scroller.closest('main');
      const top = Math.max(0, main?.getBoundingClientRect().top || 0);
      const left = Math.max(0, bounds.left + scroller.clientLeft);
      const width = Math.max(0, Math.min(bounds.right - scroller.clientLeft, document.documentElement.clientWidth) - left);
      const height = Math.min(headBounds.height, tableBounds.bottom - top);
      if (headBounds.top >= top || height <= 0 || width <= 0) {
        setPinned(current => current === null ? current : null);
        return;
      }
      const next = { top, left, width, height, tableWidth: tableBounds.width, offset: tableBounds.left - left, columns: Array.from(head.rows[0].cells, cell => cell.getBoundingClientRect().width) };
      setPinned(current => JSON.stringify(current) === JSON.stringify(next) ? current : next);
    };
    const schedule = () => { if (frame === null) frame = window.requestAnimationFrame(measure); };
    // Capture page, main-panel and horizontal table scrolling in the same place.
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    observer?.observe(table);
    observer?.observe(scroller);
    measure();
    return () => {
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, []);

  return <>
    <div ref={container} className="overflow-x-auto rounded-xl border border-slate-200">{children}</div>
    {pinned && header && createPortal(
      <div aria-hidden="true" className="overview-pinned-header" style={{ top: pinned.top, left: pinned.left, width: pinned.width, height: pinned.height }}>
        <table style={{ width: pinned.tableWidth, transform: `translateX(${pinned.offset}px)` }}>
          <colgroup>{pinned.columns.map((width, index) => <col key={index} style={{ width }} />)}</colgroup>
          {header}
        </table>
      </div>, document.body
    )}
  </>;
}
